import { useEffect, useRef } from "react";

interface UseCrossTabIdleSyncOptions {
    onActivityDetected: () => void;
    channelName?: string;
}

/**
 * Hook to synchronize idle timer activity across multiple browser tabs.
 * Uses Broadcast Channel API with localStorage fallback for older browsers.
 *
 * @param options.onActivityDetected - Callback to execute when activity is detected in another tab
 * @param options.channelName - Optional name for the broadcast channel (default: 'idle-timer-sync')
 * @returns broadcastActivity - Function to broadcast activity to other tabs
 */
export const useCrossTabIdleSync = (options: UseCrossTabIdleSyncOptions) => {
    const { onActivityDetected, channelName = "idle-timer-sync" } = options;
    const channelRef = useRef<BroadcastChannel | null>(null);
    const lastActivityRef = useRef<number>(Date.now());

    useEffect(() => {
        // Check if BroadcastChannel is supported
        const supportsBroadcastChannel = typeof BroadcastChannel !== "undefined";

        if (supportsBroadcastChannel) {
            // Use Broadcast Channel API (modern browsers)
            channelRef.current = new BroadcastChannel(channelName);

            channelRef.current.onmessage = (event) => {
                if (event.data?.type === "activity") {
                    // Activity detected in another tab, reset timer
                    onActivityDetected();
                }
            };
        } else {
            // Fallback to localStorage events (older browsers)
            const handleStorageEvent = (event: StorageEvent) => {
                if (event.key === channelName && event.newValue) {
                    try {
                        const data = JSON.parse(event.newValue);
                        if (data.type === "activity" && data.timestamp !== lastActivityRef.current) {
                            onActivityDetected();
                        }
                    } catch (e) {
                        // Ignore parsing errors
                    }
                }
            };

            window.addEventListener("storage", handleStorageEvent);

            // Cleanup
            return () => {
                window.removeEventListener("storage", handleStorageEvent);
            };
        }

        // Cleanup for Broadcast Channel
        return () => {
            if (channelRef.current) {
                channelRef.current.close();
                channelRef.current = null;
            }
        };
    }, [onActivityDetected, channelName]);

    /**
     * Broadcast activity to all other tabs
     */
    const broadcastActivity = () => {
        const timestamp = Date.now();
        lastActivityRef.current = timestamp;

        if (channelRef.current) {
            // Use Broadcast Channel API
            channelRef.current.postMessage({ type: "activity", timestamp });
        } else {
            // Fallback to localStorage
            try {
                localStorage.setItem(channelName, JSON.stringify({ type: "activity", timestamp }));
            } catch (e) {
                // Ignore storage errors
            }
        }
    };

    return { broadcastActivity };
};
